import {registerLocale} from 'react-datepicker';
import {initReactI18next} from 'react-i18next';
import {setDefaultOptions} from 'date-fns';
import {
  bg,
  cs,
  da,
  de,
  el,
  enGB,
  enUS,
  es,
  et,
  fi,
  fr,
  hr,
  hu,
  it,
  ko,
  lt,
  lv,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
  tr,
  uk,
  nb,
} from 'date-fns/locale';
import i18n from 'i18next';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import {LANGUAGE_COOKIE_KEY} from '@di-shared/features/common/useInferableTranslation';
import {RegionType} from '@shared/cci/features/const/environment';

export type SupportLanguage = (typeof SUPPORT_LANGUAGES)[number];

export type SupportLanguageMap<T> = {
  [key in SupportLanguage]: T;
};

export interface SupportLanguageRegion {
  [RegionType.KR]: (typeof SUPPORT_LANGUAGES_REGION)[RegionType.KR][number];
  [RegionType.EU]: (typeof SUPPORT_LANGUAGES_REGION)[RegionType.EU][number];
  [RegionType.NA]: (typeof SUPPORT_LANGUAGES_REGION)[RegionType.NA][number];
}

export const FALLBACK_LANGUAGE = {
  [RegionType.KR]: 'en',
  [RegionType.EU]: 'en_GB',
  [RegionType.NA]: 'en_US',
};

// 로컬라이즈의 언어 형식 기준
export const SUPPORT_LANGUAGES = [
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'en_GB',
  'en_US',
  'es',
  'et',
  'fi',
  'fr',
  'hr',
  'hu',
  'it',
  'ko',
  'lt',
  'lv',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'sk',
  'sl',
  'sv',
  'uk',
  'tr',
] as const;

export const SUPPORT_LANGUAGES_REGION = {
  [RegionType.KR]: ['ko', 'en'],
  [RegionType.NA]: ['en_US'],
  [RegionType.EU]: [
    'bg',
    'cs',
    'da',
    'de',
    'el',
    'en_GB',
    'es',
    'et',
    'fi',
    'fr',
    'hr',
    'hu',
    'it',
    'lt',
    'lv',
    'nl',
    'no',
    'pl',
    'pt',
    'ro',
    'sk',
    'sl',
    'sv',
    'uk',
    'tr',
  ],
};

// date-fns 언어 형식 기준
export const DATE_FNS_LOCALE_MAP = {
  bg,
  cs,
  da,
  de,
  el,
  en: enUS,
  en_US: enUS,
  en_GB: enGB,
  es,
  et,
  fi,
  fr,
  hr,
  hu,
  it,
  ko,
  lt,
  lv,
  nl,
  no: nb,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
  uk,
  tr,
};

const languageSelector = (language: string, namespace: string, region: string) =>
  import(`./assets/locales/${region.toLowerCase()}/${namespace}_${language}.json`);

function changeHtmlLang(language: SupportLanguage) {
  document.documentElement.lang = language;
}

function setDateFnsLocale(language: SupportLanguage) {
  const locale = DATE_FNS_LOCALE_MAP[language];
  setDefaultOptions({
    locale,
  });
}

export function getLanguageType(lang: string, region: RegionType) {
  const languageAndLocale = lang.split(/[-_]/); // ex) ko, ko-KR, en, en-US...

  let selectedLanguage = FALLBACK_LANGUAGE[region];

  const language = languageAndLocale[0]?.toLowerCase(); // en, fr, sv...
  const countryCode = languageAndLocale[1]?.toUpperCase(); // GB, FR, US...

  SUPPORT_LANGUAGES_REGION[region].forEach((key) => {
    if (key === `${language}-${countryCode}` || key === language) {
      selectedLanguage = key;
    }
  });

  return selectedLanguage;
}

export async function i18nInit(initLanguage: string, brand = 'kia', region: RegionType) {
  const languageDetector = new I18nextBrowserLanguageDetector();

  languageDetector.addDetector({
    name: 'webview',
    lookup() {
      return getLanguageType(initLanguage, region);
    },
  });

  SUPPORT_LANGUAGES.forEach((value) => {
    registerLocale(value, DATE_FNS_LOCALE_MAP[value]);
  });

  return i18n
    .use(initReactI18next)
    .use(resourcesToBackend((language: string, namespace: string) => languageSelector(language, namespace, region)))
    .use(languageDetector)
    .init({
      debug: false,
      fallbackLng: FALLBACK_LANGUAGE[region],
      ns: [brand],
      defaultNS: brand,
      interpolation: {
        escapeValue: false,
      },
      supportedLngs: SUPPORT_LANGUAGES_REGION[region].map((value) => value),
      detection: {
        order: ['webview', 'cookie', 'navigator'],
        caches: ['cookie'],
        lookupCookie: LANGUAGE_COOKIE_KEY,
      },
    })
    .catch(() => console.error('Error occurred'));
}

i18n.on('languageChanged', () => {
  const language = i18n.resolvedLanguage as SupportLanguage;
  changeHtmlLang(language);
  setDateFnsLocale(language);
});

export default i18n;
